/**
 * GameRegistry implementation maintains in-memory caches of game components, plugins,
 * and dynamically loaded game modules. Published games are resolved through the
 * catalog, so the platform does not statically import game-specific code.
 */
import { GameRegistry, GameComponent } from './gameRegistry.types'
import type { Game } from '@merels/game-logic/plugin'
import { createBgioGameWrapper } from '@merels/game-logic/bgio-wrapper'
import type { GameThemeInput } from '@merels/games/types/theme'
import type { PlayerPieceComponent } from '@merels/sdk'
import { signal } from '@preact/signals-react'
import { makeDynamicGameLoader } from '../dynamicGameLoader/dynamicGameLoader'
import type { DynamicGameLoader } from '../dynamicGameLoader/dynamicGameLoader.types'

const componentCache = new Map<string, GameComponent>()
const pluginCache = new Map<string, Game>()
const themeConfigCache = new Map<string, GameThemeInput>()

/**
 * `dynamicGameLoader`, if provided, is used as-is instead of building one
 * via `makeDynamicGameLoader(workerUrl)` — a headless Node client (see
 * gameRegistry.headless.ts) injects one built with a Node-safe module
 * importer, since the browser default calls `import()` on a URL directly,
 * which only works via a bundler/native browser ESM loader.
 */
export function makeGameRegistry(
  workerUrl: string,
  dynamicGameLoaderInput?: DynamicGameLoader,
  getAuthToken?: () => Promise<string | null>,
): GameRegistry {
  const dynamicGameLoader = dynamicGameLoaderInput ?? makeDynamicGameLoader(workerUrl, undefined, getAuthToken)
  const rulebookAvailability = signal<Record<string, boolean>>({})

  const recordRulebookAvailability = (gameId: string, available: boolean): void => {
    rulebookAvailability.value = { ...rulebookAvailability.value, [gameId]: available }
  }

  // Trigger catalog fetch and WebSocket subscription immediately on app startup
  void dynamicGameLoader.getCatalog()

  const getGameComponent = (gameId: string): GameComponent | null => {
    return componentCache.get(gameId) || null
  }

  const getGameComponentAsync = async (gameId: string): Promise<GameComponent | null> => {
    try {
      const { Game } = await dynamicGameLoader.loadGameClient(gameId)
      return Game as GameComponent
    } catch (e) {
      return componentCache.get(gameId) || null
    }
  }

  const getRulebookComponentAsync = async (gameId: string): Promise<GameComponent | null> => {
    try {
      const { Rulebook } = await dynamicGameLoader.loadGameClient(gameId)
      const component = Rulebook as GameComponent | undefined
      recordRulebookAvailability(gameId, Boolean(component))
      return component || null
    } catch (e) {
      recordRulebookAvailability(gameId, false)
      return null
    }
  }

  const getPlayerPieceComponentAsync = async (gameId: string): Promise<PlayerPieceComponent | null> => {
    try {
      const { PlayerPiece } = await dynamicGameLoader.loadGameClient(gameId)
      return PlayerPiece ?? null
    } catch {
      return null
    }
  }

  const getGamePlugin = (gameId: string): Game | null => {
    const cached = pluginCache.get(gameId)
    if (cached) return cached

    const resolved = dynamicGameLoader.getResolvedModule?.(gameId)
    if (resolved?.gameLogic) {
      const wrapped = createBgioGameWrapper(resolved.gameLogic)
      pluginCache.set(gameId, wrapped)
      return wrapped
    }
    return null
  }

  const getGamePluginAsync = async (gameId: string): Promise<Game | null> => {
    const cached = pluginCache.get(gameId)
    if (cached) return cached

    try {
      const module = await dynamicGameLoader.loadGameClient(gameId)
      if (module?.gameLogic) {
        const wrapped = createBgioGameWrapper(module.gameLogic)
        pluginCache.set(gameId, wrapped)
        return wrapped
      }
    } catch (e) {
      // ignore
    }
    return null
  }

  const getBotFactory = (gameId: string) => {
    const resolved = dynamicGameLoader.getResolvedModule?.(gameId)
    return resolved?.createBot || null
  }

  const getBotFactoryAsync = async (gameId: string) => {
    const resolved = await dynamicGameLoader.loadGameClient(gameId)
    return resolved?.createBot || null
  }

  const supportsBots = (gameId: string): boolean => {
    const resolved = dynamicGameLoader.getResolvedModule?.(gameId)
    return resolved?.createBot !== undefined
  }

  const supportsSharedScreen = (gameId: string): boolean => {
    const entry = dynamicGameLoader.getCurrentEntrySignal(gameId)?.value
    return entry?.supportsSharedScreen === true
  }

  const supportsHints = (gameId: string): boolean => {
    const resolved = dynamicGameLoader.getResolvedModule?.(gameId)
    const entry = dynamicGameLoader.getCurrentEntrySignal(gameId)?.value
    return (resolved?.options?.length ?? entry?.options?.length ?? 0) > 0
  }

  const supportsRulebook = (gameId: string): boolean => {
    return rulebookAvailability.value[gameId] === true
  }

  const getGameMetadata = (gameId: string): any => {
    const entry = dynamicGameLoader.getCurrentEntrySignal(gameId)?.value
    if (!entry) return null
    const resolved = dynamicGameLoader.getResolvedModule?.(gameId)
    return {
      displayName: entry.displayName,
      description: entry.description,
      minPlayers: entry.minPlayers ?? 2,
      maxPlayers: entry.maxPlayers ?? entry.minPlayers ?? 2,
      supportsSharedScreen: entry.supportsSharedScreen === true,
      challengeSources: entry.challengeSources,
      gameChallengeArrangements: entry.gameChallengeArrangements,
      options: resolved?.options?.length ? resolved.options : entry.options || [],
      getPlayerIds: (numPlayers: number) => {
        const pids = resolved?.playerIds ?? entry.playerIds
        if (!pids) {
          return Array.from({ length: numPlayers }, (_, i) => String(i))
        }
        if (Array.isArray(pids)) {
          return pids
        }
        return (pids as any)[numPlayers] ?? Object.values(pids)[0]
      }
    }
  }

  const getGameThemeConfig = (gameId: string): GameThemeInput | null => {
    return themeConfigCache.get(gameId) || null
  }

  const getGameThemeConfigAsync = async (gameId: string): Promise<GameThemeInput | null> => {
    try {
      const { themeConfig } = await dynamicGameLoader.loadGameClient(gameId)
      return themeConfig || null
    } catch (e) {
      // ignore
    }
    return themeConfigCache.get(gameId) || null
  }

  const getGameVersionSignal = (gameId: string) => {
    return dynamicGameLoader.getCurrentVersionSignal(gameId)
  }

  const getGameBuildInfo = (gameId: string) => {
    return dynamicGameLoader.getCurrentEntrySignal(gameId)
  }

  const getGameChannel = (): string => dynamicGameLoader.getChannel()

  const resolveGameVersion = async (gameId: string, channel: string) => {
    const entry = await dynamicGameLoader.resolveChannelVersion(gameId, channel)
    if (!entry) return null
    return {
      versionId: entry.versionId,
      clientBundleUrl: entry.clientBundleUrl,
      styleUrl: entry.styleUrl,
      metadata: {
        minPlayers: entry.minPlayers ?? 2,
        maxPlayers: entry.maxPlayers ?? entry.minPlayers ?? 2,
        displayName: entry.displayName,
        description: entry.description,
        supportsSharedScreen: entry.supportsSharedScreen,
        options: entry.options,
        playerIds: entry.playerIds,
        challengeSources: entry.challengeSources,
        gameChallengeArrangements: entry.gameChallengeArrangements,
      },
    }
  }

  const loadGameComponentAtVersion = async (gameId: string, version: { versionId: string; clientBundleUrl: string; styleUrl?: string }) => {
    const { Game, Rulebook, PlayerPiece, themeConfig } = await dynamicGameLoader.loadGameClientAtVersion(gameId, version)
    const rulebook = Rulebook as GameComponent | undefined
    recordRulebookAvailability(gameId, Boolean(rulebook))
    return { Game: Game as GameComponent, Rulebook: rulebook, PlayerPiece, themeConfig }
  }

  const loadGamePluginAtVersion = async (gameId: string, version: { versionId: string; clientBundleUrl: string; styleUrl?: string }): Promise<Game | null> => {
    const module = await dynamicGameLoader.loadGameClientAtVersion(gameId, version)
    if (module.gameLogic) {
      return createBgioGameWrapper(module.gameLogic)
    }
    return null
  }

  const getBotFactoryAtVersion = async (gameId: string, version: { versionId: string; clientBundleUrl: string; styleUrl?: string }) => {
    const module = await dynamicGameLoader.loadGameClientAtVersion(gameId, version)
    return module.createBot || null
  }

  const getGameLogicAsync = async (
    gameId: string,
    version?: { versionId: string; clientBundleUrl: string; styleUrl?: string },
  ) => {
    try {
      const module = version
        ? await dynamicGameLoader.loadGameClientAtVersion(gameId, version)
        : await dynamicGameLoader.loadGameClient(gameId)
      return module.gameLogic ?? null
    } catch (e) {
      return null
    }
  }

  const listGameVersions = (gameId: string) => {
    return dynamicGameLoader.listVersions(gameId)
  }

  return {
    gamesSignal: dynamicGameLoader.catalogEntries,
    getResolvedModule: dynamicGameLoader.getResolvedModule,
    getGameComponent,
    getGameComponentAsync,
    getRulebookComponentAsync,
    getPlayerPieceComponentAsync,
    getGamePlugin,
    getGamePluginAsync,
    getGameMetadata,
    getBotFactory,
    getBotFactoryAsync,
    supportsBots,
    supportsSharedScreen,
    supportsHints,
    supportsRulebook,
    getGameThemeConfig,
    getGameThemeConfigAsync,
    getGameVersionSignal,
    getGameBuildInfo,
    getGameChannel,
    resolveGameVersion,
    loadGameComponentAtVersion,
    loadGamePluginAtVersion,
    getBotFactoryAtVersion,
    getGameLogicAsync,
    listGameVersions,
    refreshCatalog: dynamicGameLoader.refreshCatalog,
    lookupCatalogEntries: dynamicGameLoader.lookupCatalogEntries,
  }
}
